import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { db } from "@lyder-talk/core/lib/db-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/hooks/use-auth";
import { useVideoReviewItems } from "./use-video-review-items";
import type { ReviewReminderData } from "@/lib/video-types";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * A single word that is ready for review, trimmed to what the reminder needs.
 *
 * The modal shows headwords rather than a bare count — "serendipity" tells a
 * learner why they were interrupted, "12 words" does not. Only the fields the
 * row actually renders are carried over.
 */
export interface DueVocabularyItem {
    id: string;
    word: string;
    definition: string;
    certLevel: string | null;
    /** Days past `next_review_at`. 0 means due today. */
    daysOverdue: number;
    /** How many times this word has already been reviewed. */
    reviewCount: number;
}

interface DueVocabularyRow {
    id: string;
    word: string;
    definition: string | null;
    cert_level: string | null;
    next_review_at: string;
    review_count: number | null;
}

/**
 * The due words and nothing else.
 *
 * This provider mounts on every page, and it used to read the whole deck
 * (`useSavedVocabulary`) just to filter for due rows — ~874KB at 1,000 words for
 * a list of usually a few dozen. The predicate lives in the query now (it is
 * indexed on `next_review_at`), and only the rendered columns come back.
 */
function useDueVocabulary() {
    const { userId, isAuthenticated } = useAuth();

    return useQuery<DueVocabularyRow[]>({
        queryKey: queryKeys.savedVocabulary.dueItems(userId),
        enabled: Boolean(isAuthenticated && userId),
        queryFn: async (): Promise<DueVocabularyRow[]> => {
            const { data, error } = await db.from("saved_vocabulary")
                .select("id, word, definition, cert_level, next_review_at, review_count")
                .eq("user_id", userId as string)
                .is("mastered_at", null)
                .not("next_review_at", "is", null)
                .lte("next_review_at", new Date().toISOString())
                .order("next_review_at", { ascending: true });

            if (error) throw error;
            return (data || []) as DueVocabularyRow[];
        },
        staleTime: 1000 * 30,
        refetchOnWindowFocus: false,
    });
}

/**
 * Hook that combines vocabulary and video review data for the review reminder modal.
 * Provides a unified interface for checking if there are items due for review.
 */
export function useReviewReminder(): ReviewReminderData & {
    hasItemsDue: boolean;
    vocabularyDueIds: string[];
    vocabularyDueItems: DueVocabularyItem[];
} {
    const { data: dueRows = [], isLoading: vocabLoading } = useDueVocabulary();
    const { videosDue, isLoading: videosLoading } = useVideoReviewItems();

    // Words that have been reviewed at least once AND are now due, most
    // overdue first — the reminder leads with whatever has waited longest.
    const vocabularyDueItems = useMemo(() => {
        const now = Date.now();
        return dueRows
            .map((row) => ({
                id: row.id,
                word: row.word,
                definition: row.definition ?? "",
                certLevel: row.cert_level,
                daysOverdue: Math.floor((now - new Date(row.next_review_at).getTime()) / MS_PER_DAY),
                reviewCount: row.review_count ?? 0,
            }))
            .sort((a, b) => b.daysOverdue - a.daysOverdue);
    }, [dueRows]);

    const vocabularyDueIds = useMemo(() => vocabularyDueItems.map((v) => v.id), [vocabularyDueItems]);

    const data = useMemo(() => ({
        vocabularyDue: vocabularyDueIds.length,
        vocabularyDueIds,
        videosDue,
        isLoading: vocabLoading || videosLoading,
    }), [vocabularyDueIds, videosDue, vocabLoading, videosLoading]);

    const hasItemsDue = data.vocabularyDue > 0 || data.videosDue.length > 0;

    return {
        ...data,
        hasItemsDue,
        vocabularyDueItems,
    };
}
